import React, { ReactNode, useState } from 'react';
import GalleryLoadingSpinner from '../../GalleryLoadingSpinner';
import { StatusInfoPositiveCheck } from '../../../../../_lib/components/StatusInfo/StatusInfoPositiveCheck';
import { StatusInfoError, StatusInfoIncomplete } from '../../../../../_lib/components/StatusInfo';
import { ContextMenu, ContextMenuButton } from '../../../../../_lib/components/ContextMenu';
// eslint-disable-next-line max-len
import { CollapsibleModifier } from '../../../../../components/organisms/CollapsibleOverlay/CollapsibleOverlay.modifier';
import { GalleryImage } from '../../GalleryImage';
import { Namespace } from '../../../../../translation/namespace';
import { TooltipPlacement } from '../../../../../components/atoms/Tooltip';
import { EditMetaDataForm } from '../../Forms/EditMetaDataForm';
import DeletableImageForm from '../../Forms/DeleteImageForm';
import ModalDialog from '../../../../../components/organisms/ModalDialog/ModalDialog';
import IForm from '../../../../../types/IForm';
import { EditableImage } from './EditableGallery';
import { ImageGallery, ImageState } from '../../../../../types/api/ImageType';
import { useSortableListItem } from '../../../../../_lib/components/SortableContentList';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';

type ActiveForm = {
  formType: 'edit' | 'delete';
  form?: IForm;
  image: ImageGallery;
};

export type EditableGalleryItemProps = EditableImage & {
  refetchImages: () => void;
};

export function EditableGalleryItem(props: EditableGalleryItemProps) {
  const { t } = useSonTranslation(Namespace.ImageGallery);

  const [currentForm, setCurrentForm] = useState<ActiveForm | null>(null);
  const closeModal = () => setCurrentForm(null);

  const { move, nextItemId, prevItemId, error } = useSortableListItem(props.id);

  const onSuccess = () => {
    props.refetchImages();
    closeModal();
  };

  /**
   * There can be two types of forms in the context menu:
   * - editMetaDataForm
   * - deleteForm
   * @param currentModalInfo
   */
  const getActiveForm = (currentModalInfo: ActiveForm): ReactNode => {
    if (currentModalInfo.formType === 'edit') {
      return <EditMetaDataForm form={currentModalInfo?.form} onSuccess={onSuccess} />;
    }
    if (currentModalInfo.formType === 'delete') {
      return (
        <DeletableImageForm
          model={currentModalInfo?.form}
          ariaLabel={t('deleteForm.headline')}
          onSuccess={onSuccess}
          cancelAction={closeModal}
        />
      );
    }
  };

  const getGalleryImageOrPreview = () => {
    if (props.image.imageState !== ImageState.pending) {
      return <GalleryImage src={props.image.src} alt={props.image.alt || props.image.originalFilename} />;
    }
    return <div className="image-gallery__preview-gray" />;
  };

  return (
    <>
      {getGalleryImageOrPreview()}
      {props.image.imageState === ImageState.pending && (
        <div className="image-gallery__pending-loading-spinner">
          <span className="invisible">
            {t('conversionInProgress', { originalFilename: props.image.originalFilename })}
          </span>
          <GalleryLoadingSpinner />
        </div>
      )}
      <div className="image-gallery__data-state">
        {props.metadataComplete
          ? (
        // null will remove role, maybe make role non default to status ?
        // eslint-disable-next-line jsx-a11y/aria-role
              <StatusInfoPositiveCheck message={t('dataComplete')} role={null} />
            )
          : (
              <StatusInfoIncomplete message={t('dataIncomplete')} />
            )}
      </div>

      {props.forms && (
        <div className="image-gallery__edit-button">
          <ContextMenu
            tooltipText={t('imageInteractions.edit.text', { fileName: props.image.originalFilename })}
            modifier={CollapsibleModifier.CONTEXT_MENU_CENTERED}
            tooltipPlacement={TooltipPlacement.TOP}
          >
            <ContextMenuButton
              aria-haspopup
              onClick={() => setCurrentForm({ formType: 'edit', form: props.forms?.edit, image: props.image })}
            >
              {t('editForm.edit')}
            </ContextMenuButton>
            <ContextMenuButton
              aria-haspopup
              onClick={() => setCurrentForm({ formType: 'delete', form: props.forms?.delete, image: props.image })}
            >
              {t('deleteForm.delete')}
            </ContextMenuButton>
            <ContextMenuButton
              aria-haspopup
              onClick={() => {
                if (prevItemId) {
                  move(prevItemId);
                }
              }}
              disabled={!prevItemId}
            >
              {t('moveForwardForm.moveForward')}
            </ContextMenuButton>
            <ContextMenuButton
              aria-haspopup
              onClick={() => {
                if (nextItemId) {
                  move(nextItemId);
                }
              }}
              disabled={!nextItemId}
            >
              {t('moveBackForm.moveBack')}
            </ContextMenuButton>
          </ContextMenu>
        </div>
      )}
      {error && <StatusInfoError message={t('errorResponse')} modifier="padding-top" />}
      {currentForm && (
        <ModalDialog
          key={currentForm?.form?.url}
          initialFocusIndex={currentForm.formType === 'edit' ? 2 : 1}
          closeModal={closeModal}
          headline={t(`${currentForm.formType}Form.modalHeadline`, {
            fileName: currentForm.image.originalFilename,
          })}
        >
          {getActiveForm(currentForm)}
        </ModalDialog>
      )}
    </>
  );
}
